/**
 * StageResult — the post-generation card that wraps a single rendered
 * artifact (activation / diagnostic / lesson / exit review / unit
 * summary). Picks the right header copy, next-step buttons, lesson-
 * count chip, and embedded child forms (results / exit ticket / unit
 * test) based on which stage we just landed on.
 */
import React, { useMemo } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Flag,
  GraduationCap,
  Layers,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Button } from "../ui/button";
import LessonDocument from "../LessonDocument";
import AdaptivePlanPreview from "../AdaptivePlanPreview";
import PostGenScheduleDialog from "../PostGenScheduleDialog";
import DiagnosticTrackerDownloads from "../DiagnosticTrackerDownloads";
import DiagnosticResultsForm from "./DiagnosticResultsForm";
import ExitTicketReviewForm from "./ExitTicketReviewForm";

const StageResult = ({
  activity,
  unit,
  mini,
  stage,
  diagnosticActivity,
  lesson1Activity,
  exitReviewActivity, // eslint-disable-line no-unused-vars
  lesson2Chain,
  onAgain,
  onNext,
  onSubmitResults,
  submittingResults,
  onSubmitExitReview, // eslint-disable-line no-unused-vars
  submittingExitReview,
  onGenerateLessonNext,
  onLogNextExitTicket,
  onWrapUpUnit,
  wrappingUp,
  onOpenUnitTest,
  onBack,
}) => {
  const isDiagnostic = stage === "diagnostic";
  const isLesson1 = stage === "lesson1";
  const isExitReview = stage === "exit_review";
  const isLesson2 = stage === "lesson2";
  const isUnitSummary = stage === "unit_summary";

  // Lesson-number of the activity currently on screen.
  const currentLessonIndex = useMemo(() => {
    if (isLesson1) return 1;
    if (isLesson2) {
      const me = (activity?.inputs || {}).math_engine || {};
      return Number(me.lesson_index || 2);
    }
    return null;
  }, [activity, isLesson1, isLesson2]);

  // For an exit-review, the lesson it just reviewed.
  const reviewedLessonIndex = useMemo(() => {
    if (!isExitReview) return null;
    const me = (activity?.inputs || {}).math_engine || {};
    return Number(me.reviewed_lesson_index || 1);
  }, [activity, isExitReview]);

  const totalLessonsSoFar = useMemo(() => {
    let n = lesson1Activity ? 1 : 0;
    n += (lesson2Chain || []).length;
    return n;
  }, [lesson1Activity, lesson2Chain]);

  const stageLabel = isUnitSummary
    ? "Unit summary"
    : isLesson2
    ? `Lesson ${currentLessonIndex || 2}`
    : isExitReview
    ? `Exit review after Lesson ${reviewedLessonIndex || 1}`
    : isLesson1
    ? "Lesson 1"
    : isDiagnostic
    ? "Diagnostic"
    : "Activation";

  const subline = isUnitSummary
    ? "Saved to your Library. Print it or share with your team."
    : isLesson2
    ? "Saved to your Library. Run it tomorrow, then log this lesson's exit ticket."
    : isExitReview
    ? "Saved to your Library. Tomorrow's plan is below — follow it, then come back to generate the next lesson."
    : isLesson1
    ? "Saved to your Library. Run it tomorrow, then come back to log the exit-ticket results."
    : isDiagnostic
    ? "Saved to your Library. Now enter the class results below — the engine will draft Lesson 1."
    : "Saved to your Library. Run it tomorrow, then come back to generate the diagnostic.";

  // Pull the engine-computed recommendation off the exit-review
  // activity so we can render the big call as a banner.
  const exitMeta = isExitReview
    ? (activity?.inputs || {}).math_engine || {}
    : null;
  const rec = exitMeta?.recommendation;
  const recPhrase = exitMeta?.recommendation_phrase || "";
  const recAccent =
    rec === "move_on"
      ? "bg-[#96ded1]/30 text-[#1c2d3d] border-[#96ded1]"
      : rec === "re_teach"
      ? "bg-red-100 text-red-900 border-red-300"
      : "bg-amber-100 text-amber-900 border-amber-300";

  // For Lesson 2 — show a small "branch" pill above the document.
  const l2Meta = isLesson2 ? (activity?.inputs || {}).math_engine || {} : null;
  const branchLabel = l2Meta?.branch_label || "";

  return (
    <div className="space-y-5" data-testid="math-engine-result">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Math Engine · {stageLabel} complete
            {branchLabel ? ` · ${branchLabel}` : ""}
          </p>
          <h3 className="mt-0.5 font-black text-2xl text-[#1c2d3d] tracking-tight">
            {mini
              ? `${unit.grade} · ${mini.label} · ${mini.name}`
              : `${unit.grade} · Unit ${unit.unit_number} · ${unit.name}`}
          </h3>
          <p className="text-sm text-[#4a6984]/70 mt-0.5">{subline}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isDiagnostic &&
            !isLesson1 &&
            !isExitReview &&
            !isLesson2 &&
            !isUnitSummary &&
            onNext && (
              <Button
                type="button"
                onClick={onNext}
                data-testid="math-engine-next-diagnostic"
                className="bg-[#4a6984] hover:bg-[#1c2d3d] text-white"
              >
                <ClipboardList className="h-4 w-4 mr-2" /> Generate the diagnostic
              </Button>
            )}
          {isExitReview && onGenerateLessonNext && (
            <Button
              type="button"
              onClick={onGenerateLessonNext}
              data-testid="math-engine-next-lesson-next"
              className="bg-[#4a6984] hover:bg-[#1c2d3d] text-white"
            >
              <GraduationCap className="h-4 w-4 mr-2" />
              {`Generate Lesson ${(reviewedLessonIndex || 1) + 1}`}
            </Button>
          )}
          {(isLesson1 || isLesson2 || isExitReview) && onWrapUpUnit && (
            <Button
              type="button"
              variant="outline"
              onClick={onWrapUpUnit}
              disabled={wrappingUp}
              data-testid="math-engine-wrap-up-unit"
              className="border-[#4a6984]/20"
            >
              {wrappingUp ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wrapping up…
                </>
              ) : (
                <>
                  <Flag className="h-4 w-4 mr-2" /> Wrap up the unit
                </>
              )}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={onAgain}
            data-testid="math-engine-regenerate"
            className="border-[#4a6984]/20"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Re-roll
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            data-testid="math-engine-pick-different"
            className="border-[#4a6984]/20"
          >
            Pick a different unit
          </Button>
        </div>
      </div>

      {(isLesson1 || isLesson2 || isExitReview || isUnitSummary) &&
        totalLessonsSoFar > 0 && (
          <div
            className="rounded-xl border border-[#4a6984]/15 bg-white/60 px-4 py-2 flex items-center gap-2 text-xs text-[#4a6984]"
            data-testid="math-engine-loop-progress"
          >
            <Layers className="h-3.5 w-3.5" />
            <span>
              <span className="font-bold text-[#1c2d3d]">
                {totalLessonsSoFar}
              </span>{" "}
              {totalLessonsSoFar === 1 ? "lesson" : "lessons"} taught so far in
              this unit
              {unit?.day_budget && (
                <>
                  {" "}· day budget{" "}
                  <span className="font-bold">{unit.day_budget}</span>
                </>
              )}
            </span>
          </div>
        )}

      {isExitReview && rec && (
        <div
          className={`rounded-xl border-2 px-5 py-4 flex items-center gap-3 ${recAccent}`}
          data-testid={`math-engine-recommendation-${rec}`}
        >
          <CheckCircle2 className="h-6 w-6 shrink-0" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] opacity-80">
              Tomorrow's call
            </p>
            <p className="text-xl font-black tracking-tight">{recPhrase}</p>
          </div>
        </div>
      )}

      {/* Phase 91b — surface the adaptive remediation plan (if any) on
          the exit-review screen so the teacher can preview exactly
          which expectations the engine will revisit / hold / extend
          inside the next adaptive lesson, BEFORE clicking Generate. */}
      {isExitReview && unit && (
        <AdaptivePlanPreview
          unitId={`${unit.grade} — Unit ${unit.unit_number} — ${unit.name}`}
        />
      )}

      <LessonDocument activity={activity} enableRefine={false} />

      {/* Branded tracker PDFs — appear under every diagnostic. The
          backend pre-fills both with the unit/grade/expectation codes
          and correct-answer letters from this diagnostic. */}
      {isDiagnostic && activity?.id && (
        <DiagnosticTrackerDownloads activityId={activity.id} />
      )}

      {/* Phase 88 — same post-generation Planner popup as the Generator.
          Skips silently for non-Pro users and for the synthetic
          unit-summary doc (which isn't a teachable lesson). */}
      {activity?.id && activity.output_type !== "math_unit_summary" && (
        <PostGenScheduleDialog activity={activity} />
      )}

      {isDiagnostic && diagnosticActivity && (
        <DiagnosticResultsForm
          activity={diagnosticActivity}
          onSubmit={onSubmitResults}
          submitting={submittingResults}
        />
      )}
      {(isLesson1 || isLesson2) && (
        <ExitTicketReviewForm
          lesson1Activity={activity}
          onSubmit={onLogNextExitTicket}
          submitting={submittingExitReview}
        />
      )}
      {isUnitSummary && (
        <div
          className="ae-card p-5 md:p-6 border-dashed text-center text-sm text-[#4a6984]/80"
          data-testid="math-engine-unit-summary-cta"
        >
          <p className="font-bold text-[#1c2d3d]">
            Ready for the next unit?
          </p>
          <p className="mt-1 text-xs text-[#4a6984]/75">
            Pick a different unit or mini-unit above to start a fresh
            workflow. This summary stays in your Library for reference.
          </p>
          {onOpenUnitTest && (
            <div className="mt-4">
              <Button
                type="button"
                onClick={onOpenUnitTest}
                className="bg-[#1c2d3d] hover:bg-[#0d1620] text-white"
                data-testid="math-engine-open-unit-test"
              >
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Generate summative test &amp; review sheet
              </Button>
              <p className="mt-2 text-[11px] text-[#4a6984]/70">
                K&amp;U · Thinking · Communication · Application — with a
                student review sheet to complete before the test.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StageResult;
